'use client';

import { useEffect, useRef } from 'react';

import { Modal, ModalBody, ModalSize } from '@bytel/trilogy-react';

import { Product3dViewer, Product3dViewerMessage } from '@app-types/product-3d';

import { PRODUCT_3D_VIEWER_MESSAGES } from '@constants/product-3d';

import { sendViewer3dClosingTracking, sendViewer3dColorTracking } from '@product-detail/helpers';

type Viewer3dModalProps = {
    viewer: Product3dViewer;
    initialColor: string;
    isRenewal: boolean;
    onColorChange: (color: string) => void;
    onClose: () => void;
};

export function Viewer3dModal({
    viewer,
    initialColor,
    isRenewal,
    onColorChange,
    onClose,
}: Readonly<Viewer3dModalProps>) {
    const title = `${viewer.name} en 3D`;
    const iframeRef = useRef<HTMLIFrameElement>(null);

    useEffect(() => {
        const handleMessage = ({ origin, source, data }: MessageEvent<Product3dViewerMessage>) => {
            if (
                origin === globalThis.location.origin &&
                source === iframeRef.current?.contentWindow &&
                data?.type === PRODUCT_3D_VIEWER_MESSAGES.COLOR_SELECT &&
                typeof data.color === 'string'
            ) {
                sendViewer3dColorTracking(isRenewal, data.color);
                onColorChange(data.color);
            }
        };

        globalThis.addEventListener('message', handleMessage);
        return () => globalThis.removeEventListener('message', handleMessage);
    }, [isRenewal, onColorChange]);

    const handleClose = () => {
        sendViewer3dClosingTracking(isRenewal);
        onClose();
    };

    return (
        <Modal active onClose={handleClose} title={title} size={ModalSize.LARGE} className='viewer-3d-modal'>
            <ModalBody>
                <iframe
                    ref={iframeRef}
                    src={`${viewer.url}${viewer.url.includes('?') ? '&' : '?'}${new URLSearchParams({ color: initialColor })}`}
                    title={title}
                    className='viewer-3d-frame'
                />
            </ModalBody>
        </Modal>
    );
}
